<script setup lang="ts">
import { Status as BookingStatus, ReasonCategory } from '@brighterly/lib-core-types/booking/enums'
import { Status as CustomerStatus } from '@brighterly/lib-core-types/customer/enums'
import { OrderStatus } from '@brighterly/lib-core-types/orders/enums'
import { TaskStatus, TutorCategoryType, TutorSlotType } from '@brighterly/lib-core-types/tutor/enums'

import { AppBadge, BadgeBookingStatus, BadgeCustomerStatus, BadgeOrderStatus, BadgePaymentService, BadgeReservationType, BadgeTutorCategory, BadgeTutorTask } from '@shared/ui/badges'
import { TutorBillingPaymentService } from '@shared/config'
import { AppHoverCard } from '@shared/ui/tooltips'
import { IndicatorPulse } from '@shared/ui/indicators'

defineOptions({
  name: 'DemosBadgesPage',
})

const bookingStatusesNoOwner = [
  BookingStatus.Confirmed,
  BookingStatus.InProgress,
  BookingStatus.ReservedPlanned,
  BookingStatus.Draft,
  BookingStatus.Planned,
  BookingStatus.Completed,
]

const bookingStatusesCategoryDependent = [
  BookingStatus.NoShow,
  BookingStatus.Canceled,
  BookingStatus.CanceledBad,
  BookingStatus.Rescheduled,
  BookingStatus.RescheduledBad,
]

const categories = [
  ReasonCategory.System,
  ReasonCategory.Customer,
  ReasonCategory.Tutor,
]

const customerStatuses = [
  CustomerStatus.Draft,
  CustomerStatus.Lead,
  CustomerStatus.LeadConfirmed,
  CustomerStatus.LeadNoShow,
  CustomerStatus.LeadCompleted,
  CustomerStatus.Deal,
]

const orderStatuses = [
  OrderStatus.CREATED,
  OrderStatus.PROCESSING,
  OrderStatus.SUCCEEDED,
  OrderStatus.FAILED,
  OrderStatus.REFUNDED,
  OrderStatus.CHARGEBACK,
  OrderStatus.VOIDED,
]

const tutorTasks = [
  TaskStatus.Completed,
  TaskStatus.Planned,
  TaskStatus.Canceled,
]

const tutorCategoryGroups: string[][] = [
  [TutorCategoryType.DemoMath, TutorCategoryType.DemoEla],
  [TutorCategoryType.PaidMath, TutorCategoryType.PaidEla],
  [TutorCategoryType.DemoMath, TutorCategoryType.PaidMath, TutorCategoryType.PaidEla, TutorCategoryType.DemoEla],
]

const reservationTypes = [
  TutorSlotType.DemoCatchUp,
  TutorSlotType.Paid,
  TutorSlotType.DemoPriority,
]

const paymentServices: (TutorBillingPaymentService | null)[] = [
  TutorBillingPaymentService.Gcash,
  TutorBillingPaymentService.Paypal,
  TutorBillingPaymentService.Payoneer,
  TutorBillingPaymentService.Wire,
  null,
]

const tutorBillingFlags = [
  { label: 'Checked', value: true },
  { label: 'Agreement sent', value: true },
  { label: 'Agreement signed', value: false },
  { label: 'Salesforce account', value: true },
  { label: 'Issues detected', value: false },
]
</script>

<template>
  <main class="demos-badges">
    <h1 class="demos-badges__title">
      Badges — Slots tracker
    </h1>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        AppBadge — без іконки
      </h2>
      <div class="demos-badges__row">
        <AppBadge
          color="emerald"
          text="Emerald"
        />
        <AppBadge
          color="red"
          text="Red"
        />
        <AppBadge
          color="blue"
          text="Blue"
        />
        <AppBadge
          color="amber"
          text="Amber"
        />
        <AppBadge
          color="orange"
          text="Orange"
        />
        <AppBadge
          color="slate"
          text="Slate"
        />
        <AppBadge
          color="indigo"
          text="Indigo"
        />
      </div>
    </section>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        BadgeCustomerStatus
      </h2>
      <div class="demos-badges__row">
        <BadgeCustomerStatus
          v-for="status in customerStatuses"
          :key="status"
          :status="status"
        />
      </div>
    </section>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        BadgeOrderStatus
      </h2>
      <div class="demos-badges__row">
        <BadgeOrderStatus
          v-for="status in orderStatuses"
          :key="status"
          :status="status"
        />
      </div>
    </section>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        BadgeTutorTask
      </h2>
      <div class="demos-badges__row">
        <BadgeTutorTask
          v-for="status in tutorTasks"
          :key="status"
          :status="status"
        />
      </div>
    </section>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        BadgeTutorCategory
      </h2>
      <div class="demos-badges__row">
        <BadgeTutorCategory
          v-for="(group, index) in tutorCategoryGroups"
          :key="index"
          :categories="group"
        />
      </div>
    </section>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        BadgeReservationType
      </h2>
      <div class="demos-badges__row">
        <BadgeReservationType
          v-for="status in reservationTypes"
          :key="status"
          :status="status"
        />
      </div>
    </section>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        BadgeBookingStatus — без category
      </h2>
      <div class="demos-badges__row">
        <BadgeBookingStatus
          v-for="status in bookingStatusesNoOwner"
          :key="status"
          :status="status"
        />
      </div>

      <h2 class="demos-badges__section-title">
        BadgeBookingStatus — за category
      </h2>
      <div
        v-for="status in bookingStatusesCategoryDependent"
        :key="status"
        class="demos-badges__row"
      >
        <span class="demos-badges__caption">{{ status }}:</span>
        <BadgeBookingStatus
          v-for="category in categories"
          :key="category"
          :status="status"
          :category="category"
        />
      </div>
    </section>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        IndicatorPulse
      </h2>
      <div class="demos-badges__row">
        <IndicatorPulse
          color="blue"
          label="Has changes"
        />
        <IndicatorPulse
          color="red"
          label="Has issues"
        />
      </div>
    </section>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        AppHoverCard
      </h2>
      <div class="demos-badges__row">
        <AppHoverCard placement="right">
          <IndicatorPulse
            color="red"
            label="Show billing flags"
          />
          <template #content>
            <ul class="demos-badges__hover-list">
              <li
                v-for="flag in tutorBillingFlags"
                :key="flag.label"
              >
                {{ flag.label }}: {{ flag.value ? 'Yes' : 'No' }}
              </li>
            </ul>
          </template>
        </AppHoverCard>
      </div>
    </section>

    <section class="demos-badges__section">
      <h2 class="demos-badges__section-title">
        BadgePaymentService
      </h2>
      <div class="demos-badges__row">
        <BadgePaymentService
          v-for="service in paymentServices"
          :key="service ?? 'null'"
          :service="service"
        />
      </div>
    </section>
  </main>
</template>

<style scoped lang="scss" src="./DemosBadgesPage.scss" />
